import React, { useState, useContext, useEffect, useCallback } from 'react';
import { string, func } from 'prop-types';
import classNames from 'classnames/bind';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { getCarManufactures, getCarModels } from 'utils/getCarAutosuggestions';
import { currentLanguage } from 'utils/currentLanguage';
import { isBlock } from 'utils/browserHistory';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';
import { WindowSizeContext } from 'context/WindowSizeContext';

import AutosuggestEmpty from 'components/AutosuggestEmpty';
import Hint from 'components/Hint';

import { withErrorBoundary } from 'containers/ErrorBoundary';
import CrossIcon from 'media/icons/cross.icon.svg';

import styles from './AutosuggestManu.module.scss';
const cx = classNames.bind(styles);

/**
 * Escape regex characters.
 * https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
 * @param {*} str
 */
const escapeRegexCharacters = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Get suggestion. Search in fetched data.
 * @param {*} value
 */
const getSuggestions = (value, suggestionData) => {
  const escapedValue = escapeRegexCharacters(value.trim());

  if (escapedValue === '' || !suggestionData) {
    return [];
  }

  const regex = new RegExp('(?:^|\\s)' + escapedValue, 'i');
  const suggestions = suggestionData.filter((item) => {
    return regex.test(getSuggestionValue(item));
  });

  return suggestions;
};

/**
 * Get suggestion value.
 * @param {*} suggestion
 */
const getSuggestionValue = (suggestion) => {
  return `${suggestion.manuName}`;
};

/**
 * Render suggestions.
 * @param {*} suggestion
 * @param {*} param1
 */
const renderSuggestion = (suggestion, { query }) => {
  const suggestionText = getSuggestionValue(suggestion);
  const matches = match(suggestionText, query);
  const manufactures = parse(suggestionText, matches);

  return (
    <span className={'suggestion-content'}>
      <span className="name">
        {manufactures.map((part, index) => {
          const className = part.highlight ? 'highlight' : null;

          return (
            <span className={className} key={index}>
              {part.text}
            </span>
          );
        })}
      </span>
    </span>
  );
};

// @todo: refactor isMounted.
const AutosuggestManu = ({
  className,
  type,
  pageType,
  id,
  placeholder,
  updatePartValue,
  setModelInputValue,
  setCarInputValue,
  setDataModels,
}) => {
  const [value, setValue] = useState('');
  const [isNoSuggestions, setIsNoSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const { labels } = useContext(StaticTextContext);
  const { currentCountry } = useContext(CountryContext);
  const { isMobile } = useContext(WindowSizeContext);
  const {
    setIsErrorManu,
    setIsErrorCar,
    setIsErrorPart,
    setIsManuTooltip,
    setIsErrorModel,
    setIsSelectedManu,
    setIsSelectedModel,
    setIsSelectedCarPlate,
    setIsSelectedCar,
    setCarTypeName,
    setIsEditedManuField,
    isEditedManuField,
    isSelectedCarPlate,
    isSelectedManu,
    vehicleType,
    manuName,
    manuFieldRef,
    manuId,
    modelName,
    manuCar,
    manuTruck,
    carPlate,
    setManuCar,
    setManuTruck,
    setManuName,
    setManuId,
    setModelName,
    setModelId,
    setCarName,
    setCarId,
    setPartName,
    setPartId,
    setCarPlate,
  } = useContext(SearchContext);

  const currentValue = isEditedManuField ? value : manuName;
  let _isMounted = false;
  const isCarPlateSearch = currentLanguage === 'fr';

  const isRequired = !isCarPlateSearch;

  const inputProps = {
    onFocus: () => {
      setIsManuTooltip(isMobile);
      setIsErrorManu(false);
    },
    onBlur: () => {
      setIsErrorManu(value.length > 0 && !manuId);
    },
    value: currentValue && !isSelectedCarPlate ? currentValue : '',
    className,
    type,
    id,
    placeholder,
    required: isRequired,
    disabled: currentCountry === '.com',
    autoFocus: !isBlock && !isCarPlateSearch && !manuName && !isSelectedCarPlate && window.innerWidth > 768,
  };

  const resetAllDepsSelect = useCallback(() => {
    updatePartValue('');

    setIsErrorCar(false);
    setIsErrorModel(false);
    setIsSelectedManu(false);
    setIsSelectedModel(false);
    setIsSelectedCar(false);
    setIsSelectedCarPlate(false);

    setModelInputValue('');
    setCarInputValue('');
    setCarTypeName('');

    setCarName('');
    setCarId(null);
    setModelName('');
    setModelId(null);
    setManuName('');
    setManuId(null);
    setCarPlate('');

    setPartName('');
    setPartId(null);
  }, []);

  const handleOnChange = (event, { newValue }) => {
    setValue(newValue);

    setIsEditedManuField(true);

    if (manuName || carPlate) {

      resetAllDepsSelect();
    }
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div {...containerProps}>
        {pageType === 'home' && (
          <Hint className={cx('hint')} arrow={true}>
            {labels.tipManuSelect}
          </Hint>
        )}
        <div className="react-autosuggest__suggestions-container-wrapper">
          {children}
        </div>
      </div>
    );
  };

  const getCarModelsData = (manuId) => {
    const partField = document.getElementById('search-model');

    getCarModels(manuId, currentCountry, vehicleType).then(
      ({ data: response }) => {
        setDataModels(
          response.data && response.data.array ? response.data.array : []
        );
        setIsErrorPart(false);
        setIsErrorManu(false);
        setIsSelectedManu(true);

        if (partField && !isMobile && !modelName) {
          partField.focus();
        }
      }
    );
  };

  const onSuggestionSelected = (
    event,
    { suggestion: { manuName, manuId } }
  ) => {
    setManuName(manuName);
    setManuId(manuId);
    getCarModelsData(manuId);
  };

  const getCurrentManuData = (vehicleType) => {
    if (vehicleType === 'P') {
      return manuCar;
    } else if (vehicleType === 'O') {
      return manuTruck;
    }
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    const dataManu = getCurrentManuData(vehicleType);
    const dataSuggestions = getSuggestions(value, dataManu);

    if (!dataManu) {
      return [];
    }

    setSuggestions(dataSuggestions);
    setIsNoSuggestions(dataSuggestions.length === 0);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setIsNoSuggestions(false);
  };

  const setManuContext = (data, vehicleType) => {
    if (vehicleType === 'P') {
      setManuCar(data);
    } else if (vehicleType === 'O') {
      setManuTruck(data);
    }
  };

  const setFocus = () => {
    if (!isSelectedCarPlate && window.innerWidth > 768) {
      !isBlock && !isCarPlateSearch && manuFieldRef.current.input && manuFieldRef.current.input.focus();
    }
  };

  const setAutosuggestManufactures = () => {
    if (currentCountry === '.com') {
      return;
    }
    getCarManufactures(currentCountry, vehicleType)
      .then(({ data: response }) => {
        const manufactures = response.data.array ? response.data.array : [];
        if (_isMounted) {
          setManuContext(manufactures, vehicleType);
        }
      })
      .catch(() => {
        if (_isMounted) {
          setManuContext([], vehicleType);
        }
      });
  };

  useEffect(() => {
    setFocus();
  }, [currentCountry]);

  useEffect(() => {
    if (manuId && manuName.length && pageType !== 'home') {
      setIsSelectedManu(true);
      getCarModels(manuId, currentCountry, vehicleType).then(
        ({ data: response }) => {
          setDataModels(
            response.data && response.data.array ? response.data.array : []
          );
        }
      );
    }
  }, []);

  useEffect(() => {
    const isManuCarCached = vehicleType === 'P' && manuCar && manuCar.length;
    const isManuTruckCached =
      vehicleType === 'O' && manuTruck && manuTruck.length;
    _isMounted = true;
    setIsEditedManuField(false);
    setFocus();

    if (!isManuCarCached && !isManuTruckCached) {
      setAutosuggestManufactures();
    }

    return () => {
      _isMounted = false;
    };
  }, [vehicleType]);

  return (
    <div
      className={cx('root', pageType, {
        'is-car-plate-search': isCarPlateSearch,
      })}
    >
      {isSelectedManu && !isSelectedCarPlate && manuName && (
        <button
          className={cx('cross-btn')}
          type="button"
          onClick={() => {
            setValue('');
            resetAllDepsSelect();
          }}
        >
          <CrossIcon  />
        </button>
      )}
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestionsContainer={renderSuggestionsContainer}
        onSuggestionSelected={onSuggestionSelected}
        renderSuggestion={renderSuggestion}
        inputProps={{ ...inputProps, onChange: handleOnChange }}
        id={id}
        ref={manuFieldRef}
      />
      {isNoSuggestions && <AutosuggestEmpty searchValue={value} />}
    </div>
  );
};

AutosuggestManu.propTypes = {
  className: string,
  type: string.isRequired,
  pageType: string.isRequired,
  id: string.isRequired,
  placeholder: string.isRequired,
  updatePartValue: func.isRequired,
  setModelInputValue: func.isRequired,
  setCarInputValue: func.isRequired,
  setDataModels: func.isRequired,
};

AutosuggestManu.defaultProps = {
  className: null,
};

export default withErrorBoundary(AutosuggestManu);



// WEBPACK FOOTER //
// ./src/components/AutosuggestManu/AutosuggestManu.js